import { useIsFetching } from '@tanstack/react-query';
import { BannerNotification } from '@dx-ui/osc-banner-notification';
import {
  AutoComplete,
  FormInput,
  FormInputPhone,
  FormSelect,
  normalizePhoneValue,
} from '@dx-ui/osc-form';
import { Button, Link, Text } from '@dx-ui/global-ui-prism';
import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';
import type { FieldError, FieldErrors, SubmitErrorHandler } from 'react-hook-form';
import { FormProvider, get, useForm, useWatch } from 'react-hook-form';
import type { ReactNode } from 'react';
import cx from 'classnames';
import { AddressFields } from '@dx-ui/osc-address-fields';
import { MarketingContent as MarketingContentCCS } from './marketing-content-css';
import { MarketingContent } from './marketing-content';

import {
  CreateGuestDocument,
  useBrand_CountriesQuery,
  useCreateGuestMutation,
  useFeatureTogglesQuery,
  useLanguages_DefaultCountryCodeQuery,
  useTermsQuery,
} from './queries/generated/queries';

import type { CreateGuestMutation, CreateGuestMutationVariables } from './queries/generated/types';
import { email, nameField } from './form-validation-regex';
import { Spinner } from '@dx-ui/osc-spinner';
import type { PageInfo } from '@dx-ui/config-metrics';
import type { JoinFormMetrics } from './types/join-form-analytics';
import {
  dispatchJoinSuccess,
  dispatchJoinInitiate,
  dispatchJoinPageStopError,
  dispatchJoinValidationError,
  sendErrorAnalytics,
  trackJoinModalSuccess,
  type JoinAnalytics,
  windowJoinFormMetrics,
} from './utils/join-form-analytics';
import { logError } from '@dx-ui/framework-logger';
import { generateError } from '@dx-ui/utilities-generate-error';
import unset from 'lodash/unset';
import { type DXError } from '@dx-ui/types-graphql';
import { PersonalData, TermInput } from './personal-data';
import { ENABLE_MC_TERMS } from './constants/featureToggles';
import { getIsFeatureToggleEnabled } from '@dx-ui/utilities-feature-toggles-configs';
import { useLanguage } from '@dx-ui/framework-i18n';
import { ShowHidePasswordInput } from '@dx-ui/osc-login';
import { sendReward } from '@dx-ui/framework-conductrics';
import useUsernameFieldTest from './utils/use-username-field-test';
import { SkeletonLoader } from '@dx-ui/osc-skeleton-loader';
import UsernameInput from './username';
import { useAutoLoginTest } from './utils/use-auto-login-test';
import { useEmailErrorRedesignTest } from './utils/use-email-error-redesign-test';
import { IconProps } from '@dx-ui/osc-icon';

type BannerContent = {
  message: string | ReactNode;
  type: 'success' | 'error' | 'warning';
  customIcon?: IconProps;
} | null;

const GRAPH_ERRORS = {
  WANTS_TOTP: 996,
  TOTP_REMOVED: 995,
  COMMUNICATIONS_ERROR: 1002,
  RESERVATION_LOGIN_ERROR: 22,
  RESERVATIONWITH24HRS_LOGIN_ERROR: 16,
  CONFLICT_WITH_RESOURCE: 409,
  SUCCESSFULL_REGISRATION_DELAYED_CONFIRMATION: 1000,
} as const;

const EmailSubscription = {
  PERSONALIZED_OFFERS: 'SGMT',
  HILTON_MARKETING: 'HHPRM',
  GRAND_VACATIONS: 'HGVC',
  HHONORS_EMAIL: 'CUST',
  THIRD_PARTY_MARKETING: '3PROM',
} as const;

const USMarketingOptIn = {
  hhonorsSubscriptions: [
    EmailSubscription.HHONORS_EMAIL,
    EmailSubscription.HILTON_MARKETING,
    EmailSubscription.PERSONALIZED_OFFERS,
    EmailSubscription.GRAND_VACATIONS,
  ],
  optOuts: {},
};

type PageInfoKeys = keyof PageInfo;

const FORM_ERRORS_TO_FILTER = ['alertBoxText', 'formError', 'formErrorMessage'] as PageInfoKeys[];

const oCodes = [
  'ADAPW',
  'ANDEW',
  'BRDEW',
  'CIHCW',
  'CNMP',
  'DSKEW',
  'DTITW',
  'ESHCW',
  'HHHTW',
  'HHPCW',
  'HITEW',
  'HPHCW',
  'IOSEW',
  'JNHHW',
  'NHBHW',
  'NMUPW',
  'OHWBW',
  'OHWE',
  'OHWGW',
  'PYHTW',
  'QQHTW',
  'RUHCW',
  'WAHCW',
  'WFPAW',
  'JHTNW',
  'ASPEW',
  'SMBEW',
  'PEPEW',
  'NHSPW',
  'H4LPW',
  'H4LPE',
  'EXPJW',
];

export type JoinInput = Omit<CreateGuestMutationVariables['input'], 'propCode, country'> & {
  country: string;
  marketingOffers: boolean;
  marketingConsent?: boolean;
  marketingGrandVacations?: boolean;
} & {
  [key in TermInput]?: boolean;
};

export interface Props {
  /*
   * Feature toggle on guest, disabled by default
   */
  isMarketabilityCCSEnabled?: boolean;
  language: string;
  /**
   * Enrollment OCode, required
   */
  oCode: string;
  registrationCode?: string;
  /**
   * Called after successful enrollment
   */
  onSuccess: (enrollResult: CreateGuestMutation) => void;
  analytics?: JoinAnalytics;
  metrics?: JoinFormMetrics;
  headline: ReactNode;
  isInModal?: boolean;
  origin: string;
  onSignInClick?: () => void;
}

export function JoinForm({
  isMarketabilityCCSEnabled = false,
  language,
  oCode,
  registrationCode,
  onSuccess,
  analytics,
  metrics = windowJoinFormMetrics,
  headline,
  isInModal = false,
  origin,
  onSignInClick,
}: Props) {
  const [t] = useTranslation('osc-join-form-join');
  const { t: tForm } = useTranslation('osc-join-form-form');
  const [bannerContent, setBannerContent] = useState<BannerContent>(null);
  const [marketingOptinsSelected, setMarketingOptinsSelected] = useState<number[]>([]);
  const [isMarketingOffersModalOpen, setIsMarketingOffersModalOpen] = useState(false);
  const { showUsernameField, isUsernameFieldTestLoaded } = useUsernameFieldTest();
  const { startAutoLoginTest } = useAutoLoginTest();
  const { startEmailErrorTest } = useEmailErrorRedesignTest();
  const queryLanguage = useLanguage();
  const { data: defaultCountryCodeData } = useLanguages_DefaultCountryCodeQuery({ language });
  const isJapanesePage = language === 'ja';
  const errorRef = useRef<HTMLDivElement>(null);
  const isFetching = useIsFetching() > 0;

  const form = useForm<JoinInput>({
    mode: 'onTouched',
    criteriaMode: 'all',
    defaultValues: {
      address: {},
      country: defaultCountryCodeData?.languages?.[0]?.defaultCountryCode || 'US',
      phone: {
        phoneCountry: `1US`,
      },
    },
  });

  const { control, formState, setValue, handleSubmit, setError } = form;
  const { errors, isSubmitted } = formState;
  const hasPasswordError = !!get(errors, 'password') || !!get(errors, 'confirmPassword');

  useEffect(() => {
    if (hasPasswordError) {
      sendReward('g-password-field-error');
    }
  }, [hasPasswordError]);

  const hasErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    if (defaultCountryCodeData?.languages?.[0]?.defaultCountryCode) {
      setValue('country', defaultCountryCodeData?.languages?.[0]?.defaultCountryCode);
    }
    if (isJapanesePage) {
      setValue('phone.phoneCountry', '81JP');
    }
  }, [defaultCountryCodeData, isJapanesePage, setValue]);

  const { data: featureTOgglesQuery } = useFeatureTogglesQuery({ names: [ENABLE_MC_TERMS] });
  const enableMCTerms = getIsFeatureToggleEnabled({
    featureToggleData: featureTOgglesQuery,
    name: ENABLE_MC_TERMS,
  });

  const hasPasswordFields = oCodes?.includes(oCode);

  const { data, isLoading: isLoadingCountries } = useBrand_CountriesQuery({
    language: queryLanguage,
    getMarketing: true,
    getCCSMarketing: isMarketabilityCCSEnabled,
    countryFilter: { prohibited: false },
  });

  const createGuest = useCreateGuestMutation();

  const countries =
    [...(data?.countries || [])].sort((a, b) => Intl.Collator(language).compare(a.name, b.name)) ||
    [];

  const countryValue = useWatch({ control, name: 'country' });
  const passwordValue = useWatch({ control, name: 'password' });
  const selectedCountry = countries.find(({ code }) => code === countryValue)!;
  const marketingOptIns = selectedCountry?.marketingOptin?.marketingOptinRules?.[0]?.optIns;
  const countryCode = selectedCountry?.code;

  const { data: termsData, isLoading: isLoadingTerms } = useTermsQuery(
    { language: queryLanguage, countryCode },
    { enabled: Boolean(countryCode) }
  );

  const isChina = selectedCountry?.code === 'CN';
  const isUSA = selectedCountry?.code === 'US';

  const getFieldErrors = (errors: FieldErrors<JoinInput>) => {
    const fieldErrors: { field: string; message: string }[] = [];
    Object.entries(errors).forEach(([key, value]) => {
      if (value?.message) {
        fieldErrors.push({ field: key, message: value.message });
        return;
      }
      Object.entries(value ?? {}).forEach(([innerKey, innerValue]) => {
        const message = (innerValue as FieldError)?.message;
        if (message) {
          fieldErrors.push({ field: innerKey, message });
        }
      });
    });
    return fieldErrors;
  };

  const getErrorsDisplay = () =>
    getFieldErrors(errors).map(({ field, message }) => (
      <li key={`joinPageErrorMsg${field}`}>{message}</li>
    ));

  const updateCreateGuestMutation = (formData: JoinInput, shouldAutoLogin: boolean) => {
    const {
      address,
      email,
      name,
      password,
      phone,
      country,
      marketingGrandVacations,
      marketingOffers,
      marketingConsent,
      username,
    } = formData;
    let hhonorsSubscriptions: string[] = [];
    if (isMarketabilityCCSEnabled) {
      const marketgOptinsAutoEnrolled = marketingOptIns?.filter((optin) => optin?.autoOptIn);
      if (marketgOptinsAutoEnrolled && marketgOptinsAutoEnrolled.length) {
        hhonorsSubscriptions = marketgOptinsAutoEnrolled.flatMap(
          (optin) => optin.subscriptionCodes
        );
      }
      if (marketingOptinsSelected.length && marketingOptIns && marketingOptIns.length) {
        marketingOptinsSelected.forEach((i) => {
          const codes = marketingOptIns[i]?.subscriptionCodes || [];
          const list = [...hhonorsSubscriptions, ...codes];
          hhonorsSubscriptions = [...list];
        });
      }
    } else {
      const isGreenMarketingOptIn = selectedCountry?.marketingOptIn === 'greenMarketingOptIn';
      if (isGreenMarketingOptIn) {
        hhonorsSubscriptions = [
          ...hhonorsSubscriptions,
          EmailSubscription.PERSONALIZED_OFFERS,
          EmailSubscription.HILTON_MARKETING,
        ];
        if (selectedCountry?.marketingOptInForHGV) {
          marketingGrandVacations && hhonorsSubscriptions.push(EmailSubscription.GRAND_VACATIONS);
        } else {
          hhonorsSubscriptions.push(EmailSubscription.GRAND_VACATIONS);
        }
      } else {
        marketingOffers && hhonorsSubscriptions.push(EmailSubscription.PERSONALIZED_OFFERS);
        marketingConsent && hhonorsSubscriptions.push(EmailSubscription.HILTON_MARKETING);
        marketingGrandVacations && hhonorsSubscriptions.push(EmailSubscription.GRAND_VACATIONS);
      }
      hhonorsSubscriptions.push(EmailSubscription.HHONORS_EMAIL);
    }
    address.country = country;
    const input: CreateGuestMutationVariables['input'] = {
      requiredConsent: !!termsData?.terms?.length
        ? termsData.terms.map((term) => {
            return {
              termId: term.termId,
              version: term.version,
              response: !!formData[term.termId as keyof JoinInput],
            };
          })
        : undefined,
      address: {
        ...address,
        ...(address.state !== 'address' && { state: address.state }),
        addressType: 'home',
      },
      email,
      enrollSourceCode: oCode,
      ...(registrationCode ? { registrationCode, isLandingPageRequest: true } : {}),
      name,
      password,
      phone: {
        phoneNumber: phone?.phoneNumber || '',
        phoneCountry: normalizePhoneValue(phone?.phoneCountry || ''),
        phoneType: 'home',
      },
      preferredLanguage: language === 'id' ? 'en' : language,
      privacyRequested: false,
      subscriptions:
        !isMarketabilityCCSEnabled && isUSA
          ? USMarketingOptIn
          : { hhonorsSubscriptions, optOuts: {} },
      ...(username && { username }),
    };

    return new Promise((_resolve) => {
      createGuest?.mutate([CreateGuestDocument, { language, input, autoLogin: shouldAutoLogin }], {
        onSuccess: async (data: CreateGuestMutation) => {
          const errorCode = data?.createGuest?.error?.code;
          if (errorCode) {
            switch (errorCode) {
              case GRAPH_ERRORS.SUCCESSFULL_REGISRATION_DELAYED_CONFIRMATION:
              case GRAPH_ERRORS.CONFLICT_WITH_RESOURCE:
                dispatchJoinPageStopError({
                  trackLegacy: () =>
                    metrics?.trackJoinFormPageStopErrors?.([
                      {
                        errorCode,
                        message: t(`submit_errors.${errorCode}`),
                      },
                    ]),
                  analytics,
                  payload: {
                    errorCode,
                    alertMessage: t(`submit_errors.${errorCode}`),
                  },
                });

                if (errorCode === GRAPH_ERRORS.CONFLICT_WITH_RESOURCE) {
                  const { isWarningWithLinks, isErrorWithLinks } = await startEmailErrorTest();
                  return setBannerContent({
                    message:
                      isWarningWithLinks || isErrorWithLinks ? (
                        <Trans
                          t={t}
                          i18nKey="submit_errors.409"
                          context={isWarningWithLinks ? 'warning' : 'error'}
                        >
                          <button
                            type="button"
                            className="link--base link--brand inline-block underline underline-offset-2"
                            onClick={handleSignInClick}
                          />
                          <Link
                            url={`/${language}/hilton-honors/login/forgot-information/`}
                            onClick={() => sendReward('g-error-banner-reset-click')}
                          />
                        </Trans>
                      ) : (
                        t(`submit_errors.${errorCode}`)
                      ),
                    type: isWarningWithLinks ? 'warning' : 'error',
                    customIcon: isWarningWithLinks ? { name: 'megaphone' } : undefined,
                  });
                }
                return setBannerContent({
                  message: t(`submit_errors.${errorCode}`),
                  type: 'error',
                });

              case 997:
                if (
                  isJapanesePage &&
                  data?.createGuest?.error?.notifications?.some(({ code }) => code === 512)
                ) {
                  return setBannerContent({
                    message: t(`submit_errors.997`),
                    type: 'error',
                  });
                }
                if (data?.createGuest?.error?.notifications?.some(({ code }) => code === 504)) {
                  setBannerContent(null);
                  setError(
                    'username',
                    { type: 'exists', message: tForm('username.error_exists') },
                    { shouldFocus: true }
                  );
                  return;
                }
                return setBannerContent({
                  message: t(`submit_errors.500`),
                  type: 'error',
                });
              default:
                dispatchJoinPageStopError({
                  trackLegacy: () =>
                    metrics?.trackJoinFormPageStopErrors?.([
                      {
                        errorCode,
                        message: t(`submit_errors.500`),
                      },
                    ]),
                  analytics,
                  payload: {
                    errorCode,
                    alertMessage: t(`submit_errors.500`),
                  },
                });
                return setBannerContent({
                  message: t(`submit_errors.500`),
                  type: 'error',
                });
            }
          }

          const hHonorsNumber = data?.createGuest?.data?.hhonorsNumber;
          const encodedName = window.btoa(encodeURIComponent(name.firstName));
          const encodedNum = window.btoa(hHonorsNumber || '');
          const encodedUsername = window.btoa(username || '');
          const encodedEmail = window.btoa(encodeURIComponent(email?.emailAddress ?? ''));
          const encodedEnroll = window.btoa('true');

          sessionStorage.setItem('first_name', encodedName);
          sessionStorage.setItem('hhonors', encodedNum);
          sessionStorage.setItem('email', encodedEmail);
          sessionStorage.setItem('firstEnroll', encodedEnroll);
          sessionStorage.setItem('username', encodedUsername);
          sendReward('g-XuedMeNnr5');
          if (hHonorsNumber) {
            dispatchJoinSuccess({
              trackLegacy: isInModal ? () => trackJoinModalSuccess(hHonorsNumber) : undefined,
              analytics,
              payload: { profileId: hHonorsNumber },
            });
          }
          onSuccess(data);
        },
        onError: (data) => {
          setBannerContent({
            message: t('submit_errors.500'),
            type: 'error',
          });
          logError('JOIN_FORM', generateError(data));
        },
      });
    });
  };

  const handleFormSubmit = async (formData: JoinInput) => {
    const shouldAutoLogin = await startAutoLoginTest();
    const isLegacyJoinAnalyticsEnabled = analytics?.isLegacyJoinAnalyticsEnabled ?? true;
    if (isLegacyJoinAnalyticsEnabled && window.digitalData?.page?.pageInfo) {
      FORM_ERRORS_TO_FILTER.forEach((key) => unset(window?.digitalData?.page?.pageInfo, key));
    }
    dispatchJoinInitiate({
      trackLegacy: () => metrics.trackJoinForm(),
      analytics,
    });
    updateCreateGuestMutation(formData, shouldAutoLogin)
      .then((res) => res)
      .catch((error: DXError) => {
        throw error;
      });
  };

  const handleInvalidForm: SubmitErrorHandler<JoinInput> = (fieldErrors) => {
    // hasErrors above reads errors before validation is completed, missing new errors on submit.
    // Using onInvalid ensures we get the final, updated errors.
    const errors = getFieldErrors(fieldErrors);
    if (errors.length == 0) {
      return;
    }
    const formFields = errors.map(({ field }) => field).join('|');
    const alertMessage = errors.map(({ message }) => message).join('|');
    dispatchJoinValidationError({
      trackLegacy: () =>
        sendErrorAnalytics(
          metrics,
          errors.map(({ field }) => field),
          errors.map(({ message }) => message)
        ),
      analytics,
      payload: { formFields, alertMessage },
    });
  };

  useEffect(() => {
    if (bannerContent) {
      errorRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [bannerContent]);

  const handleSignInClick = () => {
    sendReward('g-error-banner-sign-in-click');
    onSignInClick?.();
  };

  return (
    <div className="container">
      <BannerNotification
        ref={errorRef}
        status={bannerContent?.type || 'normal'}
        customIcon={bannerContent?.customIcon || undefined}
      >
        {bannerContent ? <Text>{bannerContent.message}</Text> : null}
      </BannerNotification>
      <div className={cx('mx-auto my-10 max-w-xl', { 'my-5': isInModal })}>
        {headline}
        <BannerNotification status={hasErrors && isSubmitted ? 'error' : 'normal'} className="mb-4">
          {hasErrors && isSubmitted ? (
            <div data-testid="form-errors">
              <h2 className="font-semibold">
                <span>{t('form_error')}</span>
              </h2>
              <ul className="ms-4">{getErrorsDisplay()}</ul>
            </div>
          ) : null}
        </BannerNotification>
        <FormProvider {...form}>
          <form
            noValidate
            method="post"
            onSubmit={handleSubmit(handleFormSubmit, handleInvalidForm)}
            className="flex w-full flex-col space-y-4"
          >
            <Text className={cx({ 'self-center my-2': isInModal })}>{tForm('requiredFields')}</Text>
            {isJapanesePage ? <Text>{tForm('enterHiraganaCharacters')}</Text> : null}
            <FormInput
              name="name.firstName"
              autoComplete={AutoComplete.GIVENNAME}
              label={tForm('firstName.label')}
              labelClassName="w-full"
              optional={false}
              maxLength={18}
              registerOptions={{
                required: { value: true, message: tForm('firstName.error') },
                minLength: { value: 2, message: tForm('firstName.error_minLength') },
                pattern: { value: /^\D+$/, message: tForm('firstName.error_noDigits') },
                validate: (value: string) => {
                  if (value && !nameField.test(value)) {
                    return tForm('firstName.error_noSpecialChars');
                  }
                  return true;
                },
              }}
            />
            <FormInput
              name="name.lastName"
              autoComplete={AutoComplete.FAMILYNAME}
              label={tForm('lastName.label')}
              labelClassName="w-full"
              optional={false}
              maxLength={18}
              registerOptions={{
                required: { value: true, message: tForm('lastName.error') },
                minLength: { value: 2, message: tForm('lastName.error_minLength') },
                pattern: { value: /^\D+$/, message: tForm('lastName.error_noDigits') },
                validate: (value: string) => {
                  if (value && !nameField.test(value)) {
                    return tForm('lastName.error_noSpecialChars');
                  }
                  return true;
                },
              }}
            />

            <FormInputPhone
              options={countries}
              defaultValue="1US"
              loading={isLoadingCountries}
              labelClassName="w-full"
            />

            <FormInput
              name="email.emailAddress"
              autoComplete={AutoComplete.EMAIL}
              label={tForm('email.label')}
              type="email"
              labelClassName="w-full"
              optional={false}
              maxLength={64}
              registerOptions={{
                required: { value: true, message: tForm('email.error') },
                pattern: { value: email, message: tForm('email.error') },
              }}
            />

            <FormSelect
              autoComplete={AutoComplete.COUNTRY}
              labelClassName="w-full"
              label={tForm('address.country.label')}
              name="country"
              loading={isLoadingCountries}
              disabled={isLoadingCountries}
            >
              {countries.map(({ name, code }) => (
                <option
                  key={`option-${code}`}
                  value={code}
                  {...(code === countryValue ? { selected: true } : {})}
                >
                  {name}
                </option>
              ))}
            </FormSelect>
            <AddressFields
              addressFieldsOptions={{
                addressType: 'nonBusinessShortened',
              }}
              code={countryValue}
              language={language}
            />
            {showUsernameField && isUsernameFieldTestLoaded ? (
              <UsernameInput />
            ) : !isUsernameFieldTestLoaded ? (
              <SkeletonLoader className="h-16 w-full" variant="rounded" />
            ) : null}
            {hasPasswordFields && (
              <>
                <ShowHidePasswordInput
                  autoComplete={AutoComplete.NEWPASSWORD}
                  name="password"
                  label={tForm('password.label')}
                  labelClassName="w-full"
                  hasPasswordCriteria={true}
                  isJoinForm={true}
                />
                <ShowHidePasswordInput
                  autoComplete={AutoComplete.NEWPASSWORD}
                  name="confirmPassword"
                  isJoinForm={true}
                  label={tForm('confirmPassword.label')}
                  registerOptions={{
                    validate: {
                      shouldMatch: (value) =>
                        (!!value && value === passwordValue) || tForm('confirmPassword.error'),
                    },
                    required: false,
                  }}
                  aria-label={tForm('confirmPassword.label')}
                />
              </>
            )}
            <div className="space-y-10">
              {termsData && enableMCTerms ? (
                <PersonalData termsData={termsData} isLoadingTerms={isLoadingTerms} />
              ) : null}
              {isMarketabilityCCSEnabled ? (
                <MarketingContentCCS
                  selectedCountry={selectedCountry}
                  setMarketingOptinsSelected={setMarketingOptinsSelected}
                />
              ) : (
                <MarketingContent
                  isUSA={isUSA}
                  isChina={isChina}
                  selectedCountry={selectedCountry}
                  isMarketingOffersModalOpen={isMarketingOffersModalOpen}
                  setIsMarketingOffersModalOpen={setIsMarketingOffersModalOpen}
                  language={language}
                  origin={origin}
                />
              )}

              <Button
                type="submit"
                fill="solid"
                size="lg"
                className="w-full"
                disabled={isFetching || createGuest.isPending}
                label={
                  createGuest.isPending ? (
                    <>
                      {tForm('submitting')} <Spinner className="ms-2 inline" size="sm" />
                    </>
                  ) : (
                    t('submit', { context: null })
                  )
                }
              />

              <Text size="xs">
                <Trans t={t} i18nKey="termsAndConditions_disclaimer">
                  <Link
                    url={`/${language}/hilton-honors/honors-discount-terms/`}
                    className="text--xs"
                    isNewWindow
                  />
                </Trans>
              </Text>
              <Text className="!mt-6" size="xs">
                {t('wiFi_disclaimer')}
              </Text>
              {(selectedCountry?.marketingConsent || selectedCountry?.marketingOptIn) && (
                <>
                  <Text size="xs" color="muted">
                    {t('gdprFootnote')}
                  </Text>
                  <Text className="mt-6" size="xs" color="muted">
                    {t('gdprFootnote2')}
                  </Text>
                </>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
